import React, { useState } from "react"
import { auth } from "../firebase"
import { openBillingPortal } from "../utils/billingPortal"
import "./Pricing.css"

const CHECKOUT_SESSION_URL = "/create_checkout_session"

const Pricing = ({ user, handleGoogleSignIn }) => {
  const userPlan = user?.plan || null
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null)

  const startBillingPortal = () =>
    openBillingPortal({
      user,
      onUnauthenticated: handleGoogleSignIn,
      returnUrl: `${window.location.origin}/pricing`,
    })

  // Calls our Cloud Function which creates a Stripe Checkout Session that
  // REUSES the existing stripe_customer_id when present. This prevents
  // duplicate Stripe customers when a user cancels and re-subscribes, and
  // preserves their invoice history + saved cards in the portal.
  const startCheckout = async (planKey) => {
    if (!auth.currentUser) {
      handleGoogleSignIn()
      return
    }
    setCheckoutLoadingPlan(planKey)
    try {
      const idToken = await auth.currentUser.getIdToken()
      const res = await fetch(CHECKOUT_SESSION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ plan_key: planKey }),
      })
      const data = await res.json()
      if (!res.ok || !data.url) {
        throw new Error(data.error || `HTTP ${res.status}`)
      }
      window.location.href = data.url
    } catch (err) {
      console.error("Checkout failed:", err)
      alert("Checkout could not be started: " + err.message)
      setCheckoutLoadingPlan(null)
    }
  }

  const handleSubscription = (e, plan, buttonConfig) => {
    if (buttonConfig.disabled) {
      e.preventDefault()
      return
    }
    e.preventDefault()
    if (!user) {
      handleGoogleSignIn()
      return
    }
    if (buttonConfig.manageExisting) {
      startBillingPortal()
    } else {
      startCheckout(plan.planKey)
    }
  }

  const getPlanTier = (planName) => {
    const tiers = { starter: 1, pro: 2, business: 3 }
    return tiers[planName?.toLowerCase()] || 0
  }

  const getButtonConfig = (targetPlan) => {
    if (!userPlan) {
      return { text: "Get Started →", disabled: false, className: "", manageExisting: false }
    }

    const currentTier = getPlanTier(userPlan)
    const targetTier = getPlanTier(targetPlan)

    if (currentTier === 0) {
      return { text: "Get Started →", disabled: false, className: "", manageExisting: false }
    }

    if (currentTier === targetTier) {
      return { text: "✓ Current Plan", disabled: true, className: "current-plan", manageExisting: false }
    } else if (targetTier > currentTier) {
      return { text: "Upgrade Plan →", disabled: false, className: "upgrade", manageExisting: true }
    } else {
      return { text: "Downgrade Plan →", disabled: false, className: "downgrade", manageExisting: true }
    }
  }

  const plans = [
    {
      name: "Starter",
      planKey: "starter",
      monthlyPrice: 29,
      subscribeUrl: "https://buy.stripe.com/bJe6oHcGq0uNgWz7E1c3m05",
      features: [
        "120 credits",
        "8 videos",
        "2K image generation",
        "2K video generation",
        "Ready-to-use character library",
      ],
    },
    {
      name: "Pro",
      planKey: "pro",
      monthlyPrice: 69,
      isPopular: true,
      subscribeUrl: "https://buy.stripe.com/aFa28rgWG1yR9u7bUhc3m04",
      features: [
        "300 credits",
        "20 videos",
        "2K image generation",
        "2K video generation",
        "Ready-to-use character library",
        "Trending viral motion presets",
        "Parallel generations",
      ],
    },
    {
      name: "Business",
      planKey: "business",
      monthlyPrice: 299,
      isPopular: false,
      subscribeUrl: "https://buy.stripe.com/4gM8wP49UgtLayb8I5c3m03",
      features: [
        "1350 credits",
        "90 videos",
        "2K image generation",
        "2K video generation",
        "Ready-to-use character library",
        "Trending viral motion presets",
        "Parallel generations",
        "Tailored prompt optimizer",
        "Priority customer support",
        "Feature requests",
      ],
    },
  ]

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1>Plans & pricing</h1>
      </div>
      <div className="pricing-plans">
        {plans.map((plan) => {
          const buttonConfig = getButtonConfig(plan.planKey)
          return (
            <div
              key={plan.name}
              className={`plan-card ${plan.isPopular ? "popular" : ""}`}
            >
              {plan.isPopular && <div className="popular-badge">Most popular</div>}
              <h2>{plan.name}</h2>
              <div className="price">
                ${plan.monthlyPrice}
                <span> / per month</span>
              </div>
              {buttonConfig.disabled ? (
                <button
                  disabled
                  className={`subscribe-button ${buttonConfig.className}`}
                >
                  {buttonConfig.text}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={checkoutLoadingPlan === plan.planKey}
                  onClick={(e) => handleSubscription(e, plan, buttonConfig)}
                  className={`subscribe-button ${buttonConfig.className}`}
                >
                  {checkoutLoadingPlan === plan.planKey ? "Loading..." : buttonConfig.text}
                </button>
              )}
            <ul className="features-list">
              {plan.features.map((feature, index) => {
                const isFirst = index === 0;
                let content;

                if (typeof feature === 'string') {
                  content = feature;
                } else {
                  content = (
                    <>
                      {feature.text}{' '}
                      <span className="feature-subtext">{feature.subtext}</span>
                    </>
                  );
                }
                
                return (
                  <li key={index}>
                    <span className="feature-icon">✓</span>
                    <span className="feature-text">
                      {isFirst ? <strong>{content}</strong> : content}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )})}
      </div>
      <div className="contact-info">
        For questions, write an email to <a href="mailto:supportpinkcatai@gmail.com">supportpinkcatai@gmail.com</a>
      </div>
    </div>
  )
}

export default Pricing 